/**
 * Auth API Service
 *
 * Authentication-specific API methods that replicate Angular's SiteService behavior.
 * Uses httpClient for all requests to leverage interceptors and token rotation.
 *
 * This service maintains the EXACT same API contracts as Angular:
 * - Same endpoints
 * - Same request/response formats
 * - Same error handling
 */

import { httpClient } from './http-client.service';
import { type AxiosResponse } from 'axios';

// ========================================
// TypeScript Interfaces (Type Safety)
// ========================================

export interface LoginCredentials {
  email: string;
  password: string;
  rememberMe?: boolean;
}

export interface LoginResponse {
  status: string;
  message?: string;
  data: {
    token: string;
    multiAuthentication?: boolean;
    reference_id?: string;
    isPasswordExpired?: boolean;
  };
}

export interface UserInfo {
  name: string;
  email: string;
  role: string;
  role_name: string;
  menuList: MenuItem[];
  last_logedIn: string;
  password_changed: boolean;
  ui_config?: {
    common?: any;
  };
  features?: {
    isBalanceDataIncluded?: boolean;
    // Future feature flags can be added here:
    // isTransactionDataIncluded?: boolean;
    // isReportsEnabled?: boolean;
    // etc.
  };
}

export interface MenuItem {
  code: string;
  name: string;
  view?: boolean;
  create?: boolean;
  update?: boolean;
  delete?: boolean;
  subList?: MenuItem[];
}

export interface UserInfoResponse {
  status: string;
  message?: string;
  data: UserInfo;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ForgotPasswordResponse {
  status: string;
  message?: string;
  data: {
    reference_id: string;
  };
}

export interface VerifyOTPRequest {
  reference_id: string;
  otp: string;
}

export interface VerifyOTPResponse {
  status: string;
  message?: string;
  data: {
    token: string;
    reference_id?: string;
  };
}

export interface ResetPasswordRequest {
  email: string;
  newpassword: string;
  oldpassword: string;
}

export interface ForgotPasswordResetRequest {
  email: string;
  newpassword: string;
  reference_id: string;
}

export interface ResetPasswordResponse {
  status: string;
  message?: string;
}

// ========================================
// Auth API Service Class
// ========================================

class AuthAPIService {
  /**
   * Login User
   * Endpoint: POST /user/login
   * Matches: Angular SiteService.login()
   */
  async login(credentials: LoginCredentials): Promise<AxiosResponse<LoginResponse>> {
    try {
      const response = await httpClient.post<LoginResponse>('user/login', {
        email: credentials.email,
        password: credentials.password,
      });

      return response;
    } catch (error) {
      console.error('[Auth API] Login failed');
      throw error;
    }
  }

  /**
   * Get Logged User Info
   * Endpoint: POST /user/info (Note: POST not GET, matching Angular)
   * Matches: Angular SiteService.getLoggedUserInfo()
   */
  async getUserInfo(): Promise<AxiosResponse<UserInfoResponse>> {
    try {
      // Note: Angular uses POST with empty body
      // Send empty object {} instead of empty string to match Angular behavior
      const response = await httpClient.post<UserInfoResponse>('user/info', {});

      return response;
    } catch (error) {
      console.error('[Auth API] getUserInfo failed');
      throw error;
    }
  }

  /**
   * Send Forgot Password OTP
   * Endpoint: POST /user/sendOtpMfa
   * Matches: Angular SiteService.sendOtpForgotPass()
   */
  async sendForgotPasswordOTP(
    email: string
  ): Promise<AxiosResponse<ForgotPasswordResponse>> {
    try {
      const response = await httpClient.post<ForgotPasswordResponse>('user/sendOtpMfa', {
        email,
      });

      return response;
    } catch (error) {
      console.error('[Auth API] sendForgotPasswordOTP failed');
      throw error;
    }
  }

  /**
   * Verify OTP for Forgot Password
   * Endpoint: POST /multiauthentication/otpvalidation
   * Matches: Angular SiteService.otpLogin()
   */
  async verifyForgotPasswordOTP(
    request: VerifyOTPRequest
  ): Promise<AxiosResponse<VerifyOTPResponse>> {
    try {
      const response = await httpClient.post<VerifyOTPResponse>(
        'multiauthentication/otpvalidation',
        request
      );

      return response;
    } catch (error) {
      console.error('[Auth API] verifyForgotPasswordOTP failed');
      throw error;
    }
  }

  /**
   * Verify OTP for MFA Login
   * Endpoint: POST /multiauthentication/otpvalidation
   * Matches: Angular SiteService.otpLogin()
   *
   * Same endpoint as verifyForgotPasswordOTP but used in MFA login context
   */
  async otpLogin(
    request: VerifyOTPRequest
  ): Promise<AxiosResponse<VerifyOTPResponse>> {
    try {
      const response = await httpClient.post<VerifyOTPResponse>(
        'multiauthentication/otpvalidation',
        request
      );

      return response;
    } catch (error) {
      console.error('[Auth API] otpLogin failed');
      throw error;
    }
  }

  /**
   * Change Password (regular self-service)
   * Endpoint: POST /change_password
   * Matches: Angular AdminService.updateUserPassword()
   */
  async resetPassword(
    request: ResetPasswordRequest
  ): Promise<AxiosResponse<ResetPasswordResponse>> {
    try {
      const response = await httpClient.post<ResetPasswordResponse>(
        'change_password',
        request
      );

      return response;
    } catch (error) {
      console.error('[Auth API] resetPassword failed');
      throw error;
    }
  }

  /**
   * Forgot-Password Reset (post-OTP-verification)
   * Endpoint: POST /forgot_password_reset
   * Matches: Angular AdminService.forgotPasswordReset()
   */
  async forgotPasswordReset(
    request: ForgotPasswordResetRequest
  ): Promise<AxiosResponse<ResetPasswordResponse>> {
    try {
      const response = await httpClient.post<ResetPasswordResponse>(
        'forgot_password_reset',
        request
      );

      return response;
    } catch (error) {
      console.error('[Auth API] forgotPasswordReset failed');
      throw error;
    }
  }

  /**
   * Change Password for First-Time Login
   * Endpoint: POST /reset_password
   * Matches: Angular AggregatorService.changePassword()
   * Used when new user must change password before accessing dashboard
   */
  async changePasswordFirstLogin(
    email: string,
    password: string
  ): Promise<AxiosResponse<ResetPasswordResponse>> {
    try {
      const response = await httpClient.post<ResetPasswordResponse>(
        'reset_password',
        {
          type: 'change',
          email: email,
          password: password,
        }
      );

      return response;
    } catch (error) {
      console.error('[Auth API] changePasswordFirstLogin failed');
      throw error;
    }
  }

  /**
   * SSO Login
   * Endpoint: POST /ldap/login
   * Matches: Angular SiteService.loginSSO()
   */
  async loginSSO(
    ssoData: { userId: string; password: string }
  ): Promise<AxiosResponse<LoginResponse>> {
    try {
      const response = await httpClient.post<LoginResponse>('ldap/login', ssoData);

      return response;
    } catch (error) {
      console.error('[Auth API] loginSSO failed');
      throw error;
    }
  }

  /**
   * Logout User
   * Endpoint: POST /user/logout
   * Calls logout API and handles errors gracefully
   * Allows logout to continue even if API fails (matches Angular pattern)
   */
  async logout(): Promise<void> {
    try {
      await httpClient.post('user/logout', {});
    } catch (error) {
      // Don't throw error, allow logout to continue even if API fails
      // This matches Angular pattern and current behavior
      console.warn('[Auth API] Logout API call failed (non-critical)');
    }
  }

  // ========================================
  // Token Management Methods
  // ========================================

  /**
   * Store auth token in sessionStorage
   * Matches Angular pattern
   */
  storeAuthToken(token: string): void {
    if (!token || token.trim().length === 0) {
      console.error('[Auth API] Attempted to store empty token');
      return;
    }

    sessionStorage.setItem('finpro_k_Authorization', token);
  }

  /**
   * Get auth token from sessionStorage
   */
  getAuthToken(): string | null {
    return sessionStorage.getItem('finpro_k_Authorization');
  }

  /**
   * Clear auth token from sessionStorage
   */
  clearAuthToken(): void {
    sessionStorage.removeItem('finpro_k_Authorization');
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const token = this.getAuthToken();
    return !!token && token.trim().length > 0;
  }

  // ========================================
  // User Info Management
  // ========================================

  /**
   * Store user info in sessionStorage (encrypted, matching Angular)
   */
  storeUserInfo(userInfo: UserInfo): void {
    try {
      // Encrypt user info exactly like Angular: btoa(JSON.stringify(userInfo))
      const encryptedData = btoa(JSON.stringify(userInfo));
      sessionStorage.setItem('loggedUserInfo', encryptedData);
    } catch (error) {
      console.error('[Auth API] Failed to store user info');
    }
  }

  /**
   * Get user info from sessionStorage (decrypted)
   */
  getUserInfoFromStorage(): UserInfo | null {
    try {
      const encryptedData = sessionStorage.getItem('loggedUserInfo');
      if (!encryptedData) {
        return null;
      }

      // Decrypt user info exactly like Angular: JSON.parse(atob(encryptedData))
      const userInfo = JSON.parse(atob(encryptedData)) as UserInfo;
      return userInfo;
    } catch (error) {
      console.error('[Auth API] Failed to retrieve user info');
      return null;
    }
  }

  /**
   * Clear user info from sessionStorage
   */
  clearUserInfo(): void {
    sessionStorage.removeItem('loggedUserInfo');
  }
}

// ========================================
// Export Singleton Instance
// ========================================

// Export singleton instance (matches Angular service pattern)
export const authAPI = new AuthAPIService();

// Export class for testing or custom instances
export { AuthAPIService };
